import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCourses, updateCourse, deleteCourse } from '../api';
import './Courses.css';

// Simple debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [filters, setFilters] = useState({ status: 'all' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const navigate = useNavigate();

  // Fetch all courses from backend
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCourses();
        // Preserve all course data including modules_count, published_at, etc.
        setCourses(data);
      } catch (err) {
        setError('Failed to fetch courses. Please try again.');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Handle archive/suspend
  const handleArchive = async (courseIds) => {
    if (window.confirm(`Are you sure you want to archive ${courseIds.length} course(s)? They will be hidden from active listings.`)) {
      try {
        await Promise.all(courseIds.map(id => updateCourse(id, { is_archived: true })));

        setCourses(courses.map(course =>
          courseIds.includes(course.id) ? { ...course, is_archived: true } : course
        ));
        setSelectedCourses([]);
      } catch (err) {
        setError('Failed to archive course(s). Please try again.');
        console.error('Archive error:', err);
      }
    }
  };

  // Handle delete
  const handleDelete = async (courseIds) => {
    const count = courseIds.length;
    const message = count === 1 
      ? `Are you sure you want to delete this course? This action cannot be undone and will delete all related data (modules, topics, contents, enrollments, etc.).`
      : `Are you sure you want to delete ${count} course(s)? This action cannot be undone and will delete all related data for each course.`;
    
    if (window.confirm(message)) {
      try {
        setLoading(true);
        await Promise.all(courseIds.map(id => deleteCourse(id)));

        // Remove deleted courses from the list
        setCourses(courses.filter(course => !courseIds.includes(course.id)));
        setSelectedCourses([]);
        setError(null);
      } catch (err) {
        const errorMessage = err?.message || 'Failed to delete course(s). Please try again.';
        setError(errorMessage);
        console.error('Delete error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle selection
  const toggleCourseSelection = useCallback((id) => {
    setSelectedCourses(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  }, []);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => setSearchTerm(value), 300),
    []
  );

  // Memoized filtered courses
  const filteredCourses = useMemo(() => {
    return courses.filter(course =>
      ((course.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
       (course.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
       (course.category || '').toLowerCase().includes(searchTerm.toLowerCase())) &&
      (filters.status === 'all' ||
       (filters.status === 'public' && course.is_published && !course.is_archived) ||
       (filters.status === 'draft' && !course.is_published && !course.is_archived) ||
       (filters.status === 'archived' && course.is_archived))
    );
  }, [courses, searchTerm, filters.status]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Get published date
  const getPublishedDate = (course) => {
    if (!course.is_published) return null;
    // Use updated_at if published, or check for published_at field
    return course.published_at || (course.is_published ? course.updated_at : null);
  };

  // Calculate estimated hours (placeholder - can be enhanced with actual duration data)
  const getEstimatedHours = (course) => {
    // Estimate: 1 hour per module, or use actual duration if available
    const modulesCount = course.modules_count || 0;
    return modulesCount > 0 ? modulesCount : 'N/A';
  };

  // Extract cadres/job titles from course data
  const getCourseCadres = (course) => {
    const cadres = course.cadres || course.job_titles || course.jobTitles || [];
    if (!Array.isArray(cadres)) return [];
    
    return cadres.map(cadre => {
      if (typeof cadre === 'string') return cadre;
      if (typeof cadre === 'object' && cadre !== null) {
        return cadre.name || cadre.title || cadre.cadre || 'Unknown';
      }
      return 'Unknown';
    }).filter(c => c && c !== 'Unknown');
  };

  return (
    <div className="courses-page">
      <div className="courses-header">
        <h1>Learning Courses</h1>
        <div className="action-buttons">
          <Link to="/createcourse" className="btn-primary">
            + Create Course (Step-by-Step)
          </Link>
          {selectedCourses.length === 1 && (
            <Link
              to={`/courses/edit/${selectedCourses[0]}`}
              className="btn-primary"
              aria-label="Edit selected course"
            >
              Edit Course (Step-by-Step)
            </Link>
          )}
          <button
            className="btn-primary"
            onClick={() => handleArchive(selectedCourses)}
            disabled={selectedCourses.length === 0}
            aria-label="Archive selected courses"
          >
            Archive Selected
          </button>
          <button
            className="btn-danger"
            onClick={() => handleDelete(selectedCourses)}
            disabled={selectedCourses.length === 0}
            aria-label="Delete selected courses"
          >
            Delete Selected
          </button>
        </div>
      </div>

      <div className="search-filter-bar">
        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            placeholder="Search by title, description, or category"
            onChange={e => debouncedSearch(e.target.value)}
            className="search-input"
            aria-label="Search courses"
          />
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5z" />
          </svg>
        </div>
        <div className="filter-container">
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="public">Public</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
            title="Grid view"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z"/>
            </svg>
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            aria-label="List view"
            title="List view"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zM3 6v2h18V6H3zm0 7h18v-2H3v2z"/>
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading courses...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <p>{error}</p>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" className="empty-icon">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
          </svg>
          <h3>No courses found</h3>
          <p>Try adjusting your search or filters, or <Link to="/createcourse">create a new course</Link></p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="courses-grid">
          {filteredCourses.map(course => (
            <div 
              key={course.id} 
              className={`course-card ${selectedCourses.includes(course.id) ? 'selected' : ''} ${
                course.is_archived ? 'archived' : ''
              }`}
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              <div className="course-card-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedCourses.includes(course.id)}
                  onChange={() => toggleCourseSelection(course.id)}
                  aria-label={`Select course ${course.title}`}
                />
              </div>
              <div className="course-card-image-container">
                <img
                  src={course.image_url || course.thumbnail_url || '/fallback.jpg'}
                  alt={course.title}
                  className="course-card-image"
                  onError={e => (e.target.src = '/fallback.jpg')}
                />
                <div className="course-card-overlay">
                  <span className={`status-badge-card ${
                    course.is_archived
                      ? 'status-archived'
                      : course.is_published
                      ? 'status-public'
                      : 'status-draft'
                  }`}>
                    {course.is_archived ? 'Archived' : course.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="course-card-content">
                <div className="course-card-header-row">
                  <h3 className="course-card-title">{course.title}</h3>
                  <Link
                    to={`/courses/edit/${course.id}`}
                    className="course-edit-btn"
                    onClick={(e) => e.stopPropagation()}
                    title="Edit course"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </Link>
                </div>
                <p className="course-card-description">
                  {course.description?.length > 100 
                    ? `${course.description.substring(0, 100)}...` 
                    : course.description || 'No description provided'}
                </p>
                <div className="course-card-meta">
                  <div className="meta-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>{course.category || 'Uncategorized'}</span>
                  </div>
                </div>
                {getCourseCadres(course).length > 0 && (
                  <div className="course-card-cadres">
                    <div className="cadres-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                      <span>Cadres</span>
                    </div>
                    <div className="cadres-list">
                      {getCourseCadres(course).slice(0, 3).map((cadre, idx) => (
                        <span key={idx} className="cadre-badge">{cadre}</span>
                      ))}
                      {getCourseCadres(course).length > 3 && (
                        <span className="cadre-badge-more">+{getCourseCadres(course).length - 3}</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="course-card-stats">
                  <div className="stat-item">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 7H16.5c-.8 0-1.54.5-1.85 1.26l-1.85 4.74H12v8h2v-6h1.5l.5 6H20zm-11.5 0c.83 0 1.5-.67 1.5-1.5 0-.83-.67-1.5-1.5-1.5S7 18.67 7 19.5 7.67 21 8.5 21zm-1-8L6 6H2v2h2.5l1 7h2.5z"/>
                    </svg>
                    <span className="stat-value">{course.learners_count || 0}</span>
                    <span className="stat-label">Enrolled</span>
                  </div>
                  <div className="stat-item">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span className="stat-value">{course.modules_count || 0}</span>
                    <span className="stat-label">Modules</span>
                  </div>
                  <div className="stat-item">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.12 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
                    </svg>
                    <span className="stat-value">{getEstimatedHours(course)}</span>
                    <span className="stat-label">Hours</span>
                  </div>
                </div>
                {getPublishedDate(course) && (
                  <div className="course-card-published">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    <span>Published {formatDate(getPublishedDate(course))}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="courses-table-container">
          <table className="courses-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={e => {
                      setSelectedCourses(e.target.checked ? filteredCourses.map(c => c.id) : []);
                    }}
                    checked={selectedCourses.length === filteredCourses.length && filteredCourses.length > 0}
                    aria-label="Select all courses"
                  />
                </th>
                <th>Course</th>
                <th>Category</th>
                <th>Enrollments</th>
                <th>Modules</th>
                <th>Published</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map(course => (
                <tr 
                  key={course.id}
                  className={selectedCourses.includes(course.id) ? 'selected-row' : ''}
                  onClick={() => navigate(`/courses/${course.id}`)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.id)}
                      onChange={() => toggleCourseSelection(course.id)}
                      className="course-checkbox"
                      aria-label={`Select course ${course.title}`}
                    />
                  </td>
                  <td>
                    <div className="course-name-cell">
                      <img
                        src={course.image_url || course.thumbnail_url || '/fallback.jpg'}
                        alt={course.title}
                        className="course-image"
                        onError={e => (e.target.src = '/fallback.jpg')}
                      />
                      <div>
                        <div className="course-title-row">
                          <div className="course-title">{course.title}</div>
                          <Link
                            to={`/courses/edit/${course.id}`}
                            className="course-edit-btn-table"
                            onClick={(e) => e.stopPropagation()}
                            title="Edit course"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </Link>
                        </div>
                        <div className="course-description-small">{course.description?.substring(0, 60)}...</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="course-meta-cell">
                      <span className="category-badge">{course.category || 'Uncategorized'}</span>
                      {getCourseCadres(course).length > 0 && (
                        <div className="cadres-list-inline">
                          {getCourseCadres(course).slice(0, 2).map((cadre, idx) => (
                            <span key={idx} className="cadre-badge-small">{cadre}</span>
                          ))}
                          {getCourseCadres(course).length > 2 && (
                            <span className="cadre-badge-small-more">+{getCourseCadres(course).length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="stat-cell">
                      <span className="learners-badge">{course.learners_count || 0}</span>
                    </div>
                  </td>
                  <td>
                    <div className="stat-cell">
                      <span className="modules-badge">{course.modules_count || 0}</span>
                    </div>
                  </td>
                  <td>
                    {getPublishedDate(course) ? formatDate(getPublishedDate(course)) : 'Not published'}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        course.is_archived
                          ? 'status-archived'
                          : course.is_published
                          ? 'status-public'
                          : 'status-draft'
                      }`}
                    >
                      {course.is_archived ? 'Archived' : course.is_published ? 'Public' : 'Draft'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Courses;
